import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { MoreHoriz } from '@mui/icons-material';
import Menu from '../Menu';
import { isFunction } from 'lodash';
import { useHistory } from 'react-router-dom';

export type ActionsProps<T extends { id: string }> = {
  row: T;
  handleIcon?: OverridableComponent<SvgIconTypeMap<object>>;
  actions?: Array<{
    label: string | ((row: T) => string);
    icon: OverridableComponent<SvgIconTypeMap<object>>;
    link?: string | ((row: T) => string);
    onClick?: (row: T) => void;
    disabled?: boolean | ((row: T) => boolean);
    hidden?: boolean | ((row: T) => boolean);
    tooltip?: string | ((row: T) => string | undefined);
    requiresAction?: boolean | ((row: T) => boolean);
  }>;
};

const TableActions = <T extends { id: string }>({
  actions,
  row,
  handleIcon = MoreHoriz,
}: ActionsProps<T>): JSX.Element | null => {
  const history = useHistory();
  if (!actions) return null;

  const visibleActions = actions.filter(({ hidden }) =>
    isFunction(hidden) ? !hidden(row) : !hidden,
  );

  return (
    <Menu
      handleIcon={handleIcon}
      items={visibleActions.map(
        ({ label, icon, link, onClick, disabled, tooltip, requiresAction }) => {
          const href = link ? (isFunction(link) ? link(row) : link) : undefined;
          return {
            label: isFunction(label) ? label(row) : label,
            icon,
            href,
            onClick: () => {
              if (href) {
                history.push(href);
              } else {
                onClick?.(row);
              }
            },
            disabled: isFunction(disabled) ? disabled(row) : disabled,
            tooltip: isFunction(tooltip) ? tooltip(row) : tooltip,
            requiresAction: isFunction(requiresAction)
              ? requiresAction(row)
              : requiresAction,
          };
        },
      )}
      alignWithIconRightEdge
    />
  );
};

export default TableActions;
